import React, { useState, useEffect, useCallback } from 'react';
import {
  Row, Col, Card, Statistic, Typography, Table, Tag, Spin, Tabs, Progress,
  DatePicker, Space, Button, Alert, Modal, Tooltip, message
} from 'antd';
import {
  BarChartOutlined,
  CarOutlined,
  TeamOutlined,
  WarningOutlined,
  CalendarOutlined,
  DashboardOutlined,
  ReloadOutlined,
  DeleteOutlined,
  ThunderboltOutlined,
  AimOutlined,
  ClockCircleOutlined,
  RiseOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import {
  getAnalyticsOverview,
  getAnalyticsTeams,
  getAnalyticsDaily,
  getAnalyticsDistances,
  precomputeDistances,
  clearAnalyticsCache,
} from '../../services/adminService';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

// ============================================================
// KPI Card component (same style as DashboardPage)
// ============================================================
const KpiCard = ({ title, value, suffix, icon, color, bg, precision }) => (
  <Card styles={{ body: { padding: 16 } }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
      <div style={{
        width: 44, height: 44, borderRadius: 10,
        background: bg, display: 'flex',
        alignItems: 'center', justifyContent: 'center',
        color, fontSize: 20,
      }}>
        {icon}
      </div>
      <Statistic
        title={title}
        value={value}
        suffix={suffix}
        precision={precision}
        valueStyle={{ color, fontSize: 22 }}
      />
    </div>
  </Card>
);

// ============================================================
// Score gauge color helper
// ============================================================
const scoreColor = (score) => {
  if (score >= 70) return '#52c41a';
  if (score >= 50) return '#faad14';
  return '#ff4d4f';
};

// ============================================================
// Tab 1: Overview
// ============================================================
const OverviewTab = ({ data, loading }) => {
  if (loading) return <Spin style={{ display: 'block', margin: '40px auto' }} />;
  if (!data) return <Alert message="Aucune donnée" type="info" />;

  const kpis = [
    { title: 'Dist. moy. inter-RDV', value: data.avgInterRdvDistance, suffix: 'km', icon: <CarOutlined />, color: '#0284c7', bg: '#e0f2fe' },
    { title: "Taux d'utilisation", value: Math.round(data.tauxUtilisation * 100), suffix: '%', icon: <ClockCircleOutlined />, color: '#7c3aed', bg: '#f3e8ff' },
    { title: 'Score optimisation', value: data.scoreOptimisation, suffix: '/100', icon: <RiseOutlined />, color: scoreColor(data.scoreOptimisation), bg: data.scoreOptimisation >= 70 ? '#f0fdf4' : data.scoreOptimisation >= 50 ? '#fffbeb' : '#fef2f2' },
    { title: 'Warnings distance', value: data.totalWarnings, icon: <WarningOutlined />, color: '#dc2626', bg: '#fef2f2' },
    { title: 'Total RDV', value: data.totalRdv, icon: <CalendarOutlined />, color: '#059669', bg: '#ecfdf5' },
    { title: 'RDV/jour/équipe', value: data.rdvPerDayPerTeam, icon: <AimOutlined />, color: '#0284c7', bg: '#e0f2fe', precision: 2 },
    { title: 'Taux remplissage', value: Math.round(data.tauxRemplissage * 100), suffix: '%', icon: <DashboardOutlined />, color: '#ea580c', bg: '#fff7ed' },
    { title: 'Distance totale', value: data.totalDistanceKm, suffix: 'km', icon: <CarOutlined />, color: '#6366f1', bg: '#eef2ff' },
  ];

  return (
    <div>
      <Row gutter={[12, 12]} style={{ marginBottom: 20 }}>
        {kpis.map((kpi, i) => (
          <Col xs={12} sm={6} key={i}>
            <KpiCard {...kpi} />
          </Col>
        ))}
      </Row>

      {/* Score gauge */}
      <Row gutter={[16, 16]} style={{ marginBottom: 20 }}>
        <Col xs={24} md={8}>
          <Card title="Score d'optimisation global">
            <div style={{ textAlign: 'center' }}>
              <Progress
                type="dashboard"
                percent={Math.round(data.scoreOptimisation)}
                strokeColor={scoreColor(data.scoreOptimisation)}
                format={(pct) => <span style={{ fontSize: 28, fontWeight: 600 }}>{pct}</span>}
                size={180}
              />
              <div style={{ marginTop: 12 }}>
                <Text type="secondary">
                  {data.scoreOptimisation >= 70 ? 'Bonne optimisation' :
                   data.scoreOptimisation >= 50 ? 'Optimisation moyenne' :
                   'Optimisation insuffisante'}
                </Text>
              </div>
            </div>
          </Card>
        </Col>

        <Col xs={24} md={8}>
          <Card title="Alertes - Équipes forte distance" styles={{ body: { padding: 12 } }}>
            {data.alerts?.worstTeamsByDistance?.length > 0 ? (
              data.alerts.worstTeamsByDistance.map((t, i) => (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid #f0f0f0' }}>
                  <Text>{t.team}</Text>
                  <Tag color={t.avgDistance > 100 ? 'red' : t.avgDistance > 50 ? 'orange' : 'green'}>
                    {t.avgDistance} km moy.
                  </Tag>
                </div>
              ))
            ) : <Text type="secondary">Aucune alerte</Text>}
          </Card>
        </Col>

        <Col xs={24} md={8}>
          <Card title="Jours sous-utilisés" styles={{ body: { padding: 12 } }}>
            {data.alerts?.underUtilizedDays?.length > 0 ? (
              data.alerts.underUtilizedDays.map((d, i) => (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid #f0f0f0' }}>
                  <Text>{new Date(d.date).toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short' })}</Text>
                  <Space>
                    <Tag>{d.rdvCount} RDV</Tag>
                    <Tag color="orange">{Math.round(d.avgUtilisation * 100)}%</Tag>
                  </Space>
                </div>
              ))
            ) : <Text type="secondary">Aucun jour sous-utilisé</Text>}
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]}>
        <Col span={24}>
          <Card size="small">
            <Space size="large">
              <Text type="secondary">Période: {data.period?.dateDebut} → {data.period?.dateFin}</Text>
              <Text type="secondary">Jours ouvrés: {data.workingDays}</Text>
              <Text type="secondary">Équipes actives: {data.activeTeamCount}</Text>
              <Text type="secondary">Équipes avec RDV: {data.teamsWithRdvCount}</Text>
              <Text type="secondary">% trajet dépôt: {data.depotDistancePct}%</Text>
            </Space>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

// ============================================================
// Tab 2: Teams
// ============================================================
const TeamsTab = ({ data, loading }) => {
  const [detailTeam, setDetailTeam] = useState(null);

  if (loading) return <Spin style={{ display: 'block', margin: '40px auto' }} />;
  if (!data) return <Alert message="Aucune donnée" type="info" />;

  const columns = [
    {
      title: 'Équipe', dataIndex: 'team', key: 'team', fixed: 'left', width: 130,
      render: (text, r) => <Tag color={r.couleur || '#666'}>{text}</Tag>
    },
    { title: 'Dépôt', dataIndex: 'depotNom', key: 'depotNom', width: 80 },
    { title: 'RDV', dataIndex: 'totalRdv', key: 'totalRdv', width: 70, sorter: (a, b) => a.totalRdv - b.totalRdv },
    { title: 'RDV/jour', dataIndex: 'avgRdvPerDay', key: 'avgRdvPerDay', width: 90, sorter: (a, b) => a.avgRdvPerDay - b.avgRdvPerDay },
    { title: 'Dist. totale (km)', dataIndex: 'totalDistanceKm', key: 'totalDistanceKm', width: 120, sorter: (a, b) => a.totalDistanceKm - b.totalDistanceKm },
    {
      title: 'Dist. moy. inter-RDV', dataIndex: 'avgInterRdvDistance', key: 'avgInterRdvDistance', width: 140,
      sorter: (a, b) => a.avgInterRdvDistance - b.avgInterRdvDistance,
      render: (v) => <Tag color={v > 100 ? 'red' : v > 50 ? 'orange' : 'green'}>{v} km</Tag>
    },
    {
      title: 'Utilisation', dataIndex: 'avgUtilisation', key: 'avgUtilisation', width: 140,
      sorter: (a, b) => a.avgUtilisation - b.avgUtilisation,
      render: (v) => <Progress percent={Math.round(v * 100)} size="small" strokeColor={v >= 0.7 ? '#52c41a' : v >= 0.5 ? '#faad14' : '#ff4d4f'} />
    },
    {
      title: 'Score', dataIndex: 'score', key: 'score', width: 80,
      sorter: (a, b) => a.score - b.score, defaultSortOrder: 'descend',
      render: (v) => <Tag color={scoreColor(v)}>{v}</Tag>
    },
    { title: 'Warn.', dataIndex: 'totalWarnings', key: 'totalWarnings', width: 60 },
  ];

  return (
    <div>
      {/* Utilisation bars */}
      <Card title="Taux d'utilisation par équipe" style={{ marginBottom: 16 }}>
        <Row gutter={[8, 8]}>
          {data.map((t) => (
            <Col xs={12} sm={8} md={6} key={t.team}>
              <div style={{ marginBottom: 4 }}>
                <Text style={{ fontSize: 12 }}>{t.team}</Text>
                <Tag color={scoreColor(t.score)} style={{ float: 'right', fontSize: 11 }}>{t.score}</Tag>
              </div>
              <Progress
                percent={Math.round(t.avgUtilisation * 100)}
                size="small"
                strokeColor={t.avgUtilisation >= 0.7 ? '#52c41a' : t.avgUtilisation >= 0.5 ? '#faad14' : '#ff4d4f'}
              />
            </Col>
          ))}
        </Row>
      </Card>

      <Table
        dataSource={data}
        columns={columns}
        rowKey="team"
        size="small"
        pagination={false}
        scroll={{ x: 1000 }}
        onRow={(record) => ({
          onClick: () => setDetailTeam(record),
          style: { cursor: 'pointer' }
        })}
      />

      <Modal
        title={`Détail - ${detailTeam?.team}`}
        open={!!detailTeam}
        onCancel={() => setDetailTeam(null)}
        footer={null}
        width={600}
      >
        {detailTeam && (
          <div>
            <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
              <Col span={8}><Statistic title="Total RDV" value={detailTeam.totalRdv} /></Col>
              <Col span={8}><Statistic title="Jours travaillés" value={detailTeam.daysWorked} /></Col>
              <Col span={8}><Statistic title="Score" value={detailTeam.score} suffix="/100" /></Col>
            </Row>
            <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
              <Col span={8}><Statistic title="Dist. totale" value={detailTeam.totalDistanceKm} suffix="km" /></Col>
              <Col span={8}><Statistic title="Dist. moy. inter" value={detailTeam.avgInterRdvDistance} suffix="km" /></Col>
              <Col span={8}><Statistic title="Utilisation" value={Math.round(detailTeam.avgUtilisation * 100)} suffix="%" /></Col>
            </Row>
            {detailTeam.bestDay && (
              <Alert
                type="success"
                message={`Meilleur jour: ${detailTeam.bestDay.date} - ${detailTeam.bestDay.rdvCount} RDV, ${detailTeam.bestDay.distanceKm} km`}
                style={{ marginBottom: 8 }}
              />
            )}
            {detailTeam.worstDay && (
              <Alert
                type="warning"
                message={`Pire jour: ${detailTeam.worstDay.date} - ${detailTeam.worstDay.rdvCount} RDV, ${detailTeam.worstDay.distanceKm} km`}
              />
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

// ============================================================
// Tab 3: Daily
// ============================================================
const DailyTab = ({ data, loading }) => {
  if (loading) return <Spin style={{ display: 'block', margin: '40px auto' }} />;
  if (!data) return <Alert message="Aucune donnée" type="info" />;

  const columns = [
    {
      title: 'Date', dataIndex: 'date', key: 'date', width: 130,
      render: (d) => new Date(d).toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short' })
    },
    { title: 'RDV', dataIndex: 'totalRdv', key: 'totalRdv', width: 60, sorter: (a, b) => a.totalRdv - b.totalRdv },
    { title: 'Équipes', dataIndex: 'activeTeams', key: 'activeTeams', width: 80 },
    { title: 'Dist. totale (km)', dataIndex: 'totalDistanceKm', key: 'totalDistanceKm', width: 120, sorter: (a, b) => a.totalDistanceKm - b.totalDistanceKm },
    { title: 'Dist. moy/éq. (km)', dataIndex: 'avgDistPerTeam', key: 'avgDistPerTeam', width: 120 },
    {
      title: 'Utilisation', dataIndex: 'avgUtilisation', key: 'avgUtilisation', width: 120,
      render: (v) => <Progress percent={Math.round(v * 100)} size="small" strokeColor={v >= 0.7 ? '#52c41a' : v >= 0.5 ? '#faad14' : '#ff4d4f'} />
    },
    {
      title: 'Score', dataIndex: 'score', key: 'score', width: 70,
      render: (v) => <Tag color={scoreColor(v)}>{v}</Tag>
    },
    { title: 'Warn.', dataIndex: 'totalWarnings', key: 'totalWarnings', width: 60 },
  ];

  const expandedRowRender = (record) => {
    const teamCols = [
      { title: 'Équipe', dataIndex: 'team', key: 'team' },
      { title: 'RDV', dataIndex: 'rdvCount', key: 'rdvCount' },
      { title: 'Dist. (km)', dataIndex: 'totalDistanceKm', key: 'totalDistanceKm' },
      { title: 'Dist. inter-RDV (km)', dataIndex: 'avgInterRdvKm', key: 'avgInterRdvKm' },
      {
        title: 'Utilisation', dataIndex: 'tauxUtilisation', key: 'tauxUtilisation',
        render: (v) => <Progress percent={Math.round(v * 100)} size="small" />
      },
      { title: 'Warn.', dataIndex: 'warnings', key: 'warnings' },
    ];
    return (
      <Table
        dataSource={record.teams}
        columns={teamCols}
        rowKey="team"
        size="small"
        pagination={false}
      />
    );
  };

  return (
    <Table
      dataSource={data}
      columns={columns}
      rowKey="date"
      size="small"
      pagination={{ pageSize: 15 }}
      expandable={{ expandedRowRender }}
      scroll={{ x: 800 }}
    />
  );
};

// ============================================================
// Tab 4: Distances
// ============================================================
const DistancesTab = ({ data, loading, dateDebut, dateFin }) => {
  const [precomputing, setPrecomputing] = useState(false);

  if (loading) return <Spin style={{ display: 'block', margin: '40px auto' }} />;
  if (!data) return <Alert message="Aucune donnée" type="info" />;

  const maxBucket = Math.max(...data.distribution.map(b => b.count), 1);

  const handlePrecompute = async () => {
    setPrecomputing(true);
    try {
      const result = await precomputeDistances({ date_debut: dateDebut, date_fin: dateFin });
      message.success(`${result.data.newlyComputed} nouvelles distances calculées (${result.data.alreadyCached} déjà en cache, ${result.data.errors} erreurs)`);
    } catch (err) {
      message.error('Erreur lors du pré-calcul');
    } finally {
      setPrecomputing(false);
    }
  };

  const worstColumns = [
    {
      title: 'Date', dataIndex: 'date', key: 'date', width: 100,
      render: (d) => new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' })
    },
    { title: 'Équipe', dataIndex: 'team', key: 'team', width: 110 },
    { title: 'De', dataIndex: 'from', key: 'from', width: 100 },
    { title: 'Vers', dataIndex: 'to', key: 'to', width: 100 },
    {
      title: 'Distance', dataIndex: 'distanceKm', key: 'distanceKm', width: 90,
      render: (v) => <Tag color={v > 100 ? 'red' : 'orange'}>{v} km</Tag>
    },
    { title: 'Durée (min)', dataIndex: 'durationMin', key: 'durationMin', width: 80 },
    {
      title: 'Type', dataIndex: 'isDepotLeg', key: 'isDepotLeg', width: 80,
      render: (v) => v ? <Tag>Dépôt</Tag> : <Tag color="blue">Inter-RDV</Tag>
    },
  ];

  return (
    <div>
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        {/* Distribution */}
        <Col xs={24} md={12}>
          <Card title="Distribution des distances par segment">
            {data.distribution.map((b) => (
              <div key={b.label} style={{ marginBottom: 8 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 2 }}>
                  <Text style={{ fontSize: 13 }}>{b.label}</Text>
                  <Text style={{ fontSize: 13 }}>{b.count} segments</Text>
                </div>
                <div style={{ display: 'flex', gap: 2, height: 20 }}>
                  <Tooltip title={`Inter-RDV: ${b.isInterRdv}`}>
                    <div style={{
                      width: `${(b.isInterRdv / maxBucket) * 100}%`,
                      background: '#3b82f6', borderRadius: 3, minWidth: b.isInterRdv > 0 ? 4 : 0
                    }} />
                  </Tooltip>
                  <Tooltip title={`Dépôt: ${b.isDepot}`}>
                    <div style={{
                      width: `${(b.isDepot / maxBucket) * 100}%`,
                      background: '#94a3b8', borderRadius: 3, minWidth: b.isDepot > 0 ? 4 : 0
                    }} />
                  </Tooltip>
                </div>
              </div>
            ))}
            <div style={{ marginTop: 12, display: 'flex', gap: 16 }}>
              <Space><div style={{ width: 12, height: 12, background: '#3b82f6', borderRadius: 2 }} /><Text style={{ fontSize: 12 }}>Inter-RDV</Text></Space>
              <Space><div style={{ width: 12, height: 12, background: '#94a3b8', borderRadius: 2 }} /><Text style={{ fontSize: 12 }}>Dépôt</Text></Space>
            </div>
          </Card>
        </Col>

        {/* Depot & inter-RDV stats */}
        <Col xs={24} md={12}>
          <Card title="Statistiques trajet dépôt" style={{ marginBottom: 16 }}>
            <Row gutter={[12, 12]}>
              <Col span={12}><Statistic title="Moy. dépôt → 1er RDV" value={data.depotStats.avgDepotToFirst} suffix="km" /></Col>
              <Col span={12}><Statistic title="Moy. dernier → dépôt" value={data.depotStats.avgLastToDepot} suffix="km" /></Col>
              <Col span={12}><Statistic title="Total km dépôt" value={data.depotStats.totalDepotKm} suffix="km" /></Col>
              <Col span={12}><Statistic title="% trajet dépôt" value={data.depotStats.depotPct} suffix="%" /></Col>
            </Row>
          </Card>
          <Card title="Stats inter-RDV">
            <Row gutter={[12, 12]}>
              <Col span={8}><Statistic title="Total km" value={data.interRdvStats.totalInterRdvKm} /></Col>
              <Col span={8}><Statistic title="Moy. km" value={data.interRdvStats.avgInterRdvKm} /></Col>
              <Col span={8}><Statistic title="Max km" value={data.interRdvStats.maxInterRdvKm} /></Col>
            </Row>
          </Card>
        </Col>
      </Row>

      {/* Worst segments table */}
      <Card title="Top 20 pires segments (par distance)" style={{ marginBottom: 16 }}>
        <Table
          dataSource={data.worstSegments}
          columns={worstColumns}
          rowKey={(r, i) => i}
          size="small"
          pagination={false}
          scroll={{ x: 700 }}
        />
      </Card>

      {/* Cache stats */}
      <Card title="Cache distances">
        <Row gutter={[12, 12]} align="middle">
          <Col span={6}><Statistic title="Distances en cache" value={data.cacheStats.totalCachedDistances} /></Col>
          <Col span={6}><Statistic title="Paires uniques (période)" value={data.cacheStats.uniquePairsInPeriod} /></Col>
          <Col span={6}><Statistic title="Segments calculés" value={data.totalSegments} /></Col>
          <Col span={6}>
            <Button
              type="primary"
              icon={<ThunderboltOutlined />}
              loading={precomputing}
              onClick={handlePrecompute}
            >
              Pré-calculer manquantes
            </Button>
          </Col>
        </Row>
      </Card>
    </div>
  );
};

// ============================================================
// Main AnalyticsPage
// ============================================================
const AnalyticsPage = () => {
  const [dateRange, setDateRange] = useState([
    dayjs().startOf('month'),
    dayjs().endOf('month'),
  ]);
  const [activeTab, setActiveTab] = useState('overview');
  const [overviewData, setOverviewData] = useState(null);
  const [teamsData, setTeamsData] = useState(null);
  const [dailyData, setDailyData] = useState(null);
  const [distancesData, setDistancesData] = useState(null);
  const [loading, setLoading] = useState({});

  const dateDebut = dateRange[0]?.format('YYYY-MM-DD');
  const dateFin = dateRange[1]?.format('YYYY-MM-DD');

  const fetchTab = useCallback(async (tab, forceRefresh = false) => {
    if (!dateDebut || !dateFin) return;
    const params = { date_debut: dateDebut, date_fin: dateFin };
    if (forceRefresh) params.force_refresh = '1';

    setLoading(prev => ({ ...prev, [tab]: true }));
    try {
      switch (tab) {
        case 'overview': {
          const res = await getAnalyticsOverview(params);
          setOverviewData(res.data);
          break;
        }
        case 'teams': {
          const res = await getAnalyticsTeams(params);
          setTeamsData(res.data);
          break;
        }
        case 'daily': {
          const res = await getAnalyticsDaily(params);
          setDailyData(res.data);
          break;
        }
        case 'distances': {
          const res = await getAnalyticsDistances(params);
          setDistancesData(res.data);
          break;
        }
        default: break;
      }
    } catch (err) {
      message.error(`Erreur chargement ${tab}: ${err.message}`);
    } finally {
      setLoading(prev => ({ ...prev, [tab]: false }));
    }
  }, [dateDebut, dateFin]);

  // Load active tab on date change or tab change
  useEffect(() => {
    fetchTab(activeTab);
  }, [activeTab, dateDebut, dateFin, fetchTab]);

  const handleClearCache = async () => {
    try {
      await clearAnalyticsCache();
      message.success('Cache analytics vidé');
      fetchTab(activeTab, true);
    } catch {
      message.error('Erreur lors du vidage du cache');
    }
  };

  const handleRefresh = () => {
    fetchTab(activeTab, true);
  };

  const presetRanges = {
    'Cette semaine': [dayjs().startOf('week'), dayjs().endOf('week')],
    'Ce mois': [dayjs().startOf('month'), dayjs().endOf('month')],
    'Mois dernier': [dayjs().subtract(1, 'month').startOf('month'), dayjs().subtract(1, 'month').endOf('month')],
    '3 derniers mois': [dayjs().subtract(3, 'month').startOf('month'), dayjs().endOf('month')],
  };

  const tabItems = [
    {
      key: 'overview',
      label: <span><BarChartOutlined /> Vue d'ensemble</span>,
      children: <OverviewTab data={overviewData} loading={loading.overview} />
    },
    {
      key: 'teams',
      label: <span><TeamOutlined /> Par équipe</span>,
      children: <TeamsTab data={teamsData} loading={loading.teams} />
    },
    {
      key: 'daily',
      label: <span><CalendarOutlined /> Par jour</span>,
      children: <DailyTab data={dailyData} loading={loading.daily} />
    },
    {
      key: 'distances',
      label: <span><CarOutlined /> Distances</span>,
      children: <DistancesTab data={distancesData} loading={loading.distances} dateDebut={dateDebut} dateFin={dateFin} />
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
        <Title level={4} style={{ margin: 0 }}>Analytics / Optimisation</Title>
        <Space wrap>
          <RangePicker
            value={dateRange}
            onChange={(dates) => dates && setDateRange(dates)}
            presets={Object.entries(presetRanges).map(([label, value]) => ({ label, value }))}
            format="DD/MM/YYYY"
          />
          <Tooltip title="Forcer le recalcul">
            <Button icon={<ReloadOutlined />} onClick={handleRefresh} />
          </Tooltip>
          <Tooltip title="Vider le cache">
            <Button icon={<DeleteOutlined />} onClick={handleClearCache} danger />
          </Tooltip>
        </Space>
      </div>

      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        items={tabItems}
        size="large"
      />
    </div>
  );
};

export default AnalyticsPage;
